/* global YT */
import videojs from 'video.js';

export class EmbedOverlay extends videojs.getComponent('Component') {
  constructor(player, options) {
    super(player, options);
    this.player = player;
    this.on(player, 'player:will-set-variant', (_, { variant }) => {
      this.update(variant);
    });
    this.el().classList.add('wrp-player-embed-overlay');
  }

  update(variant = {}) {
    this.clear();
    if (variant.is_placeholder && variant.embed_url) {
      const url = variant.embed_url;

      if (this.isYouTube(url)) {
        this.loadYouTubeVideo(url, variant.label);
      } else if (this.isTikTok(url)) {
        this.loadTikTokVideo(url, variant.label);
      } else if (this.isVimeo(url)) {
        this.loadVimeoVideo(url);
      } else if (this.isLoom(url)) {
        this.loadLoomVideo(url);
      } else if (this.isInstagram(url)) {
        this.loadInstagramContent(url);
      } else if (this.isLinkedIn(url)) {
        this.loadLinkedInPost(url);
      } else if (this.isIframeEmbed(url)) {
        this.loadIframeEmbed(url);
      } else if (this.isInstagramBlockquote(url)) {
        this.loadInstagramBlockquote(url);
      } else if (this.isTikTokBlockquote(url)) {
        this.loadTikTokBlockquote(url);
      } else {
        this.fallbackEmbed(url);
      }
    }
  }

  clear() {
    this.el().innerHTML = '';
  }

  // YouTube handling (using youtube-nocookie.com)
  isYouTube(url) {
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  loadYouTubeVideo(url, variantId) {
    const videoId = this.extractYouTubeId(url);
    if (videoId) {
      const youtubePlayer = document.createElement('div');
      youtubePlayer.id = `youtube-player-${variantId}`; // Create a div with a variant-specific id
      youtubePlayer.classList.add('embed-iframe');
      this.el().appendChild(youtubePlayer);

      // Extract any URL parameters and add default settings for autoplay, mute, etc.
      const urlParams = new URLSearchParams(url.split('?')[1] || '');

      // Convert 't' parameter to 'start' if it exists
      if (urlParams.has('t')) {
        const timeValue = urlParams.get('t');
        const seconds = this.convertTimeToSeconds(timeValue);
        urlParams.delete('t');
        urlParams.set('start', seconds.toString());
      }

      urlParams.set('autoplay', '1'); // Ensure autoplay is on
      urlParams.set('mute', '0'); // Ensure mute is off by default
      urlParams.set('rel', '0'); // Disable related videos
      urlParams.set('fs', '0'); // Disable fullscreen button
      urlParams.set('playsinline', '1'); // Enable inline play on mobile

      const playerVars = Object.fromEntries(urlParams.entries()); // Convert URL params to object

      const createPlayer = () => {
        this.ytPlayer = new YT.Player(youtubePlayer.id, {
          videoId: videoId,
          playerVars: playerVars, // Pass extracted params with defaults
          events: {
            onReady: this.onYTPlayerReady.bind(this),
          },
        });
      };

      // Load YouTube API script if not already loaded
      if (!window.YT) {
        if (!document.getElementById('youtube-api-script')) {
          const script = document.createElement('script');
          script.id = 'youtube-api-script';
          script.src = 'https://www.youtube.com/iframe_api';
          document.body.appendChild(script);
        }

        // Create YouTube player once API is ready
        window.onYouTubeIframeAPIReady = createPlayer;
      } else if (window.YT && window.YT.Player) {
        // If YT is already available, directly create the player
        createPlayer();
      }
    }
  }

  convertTimeToSeconds(timeValue) {
    // If the timeValue is already in plain seconds (numeric only), parse it directly
    if (/^\d+$/.test(timeValue)) {
      return parseInt(timeValue, 10);
    }

    // Otherwise, parse based on 'h', 'm', 's' format
    const timeMatch = timeValue.match(/(\d+)(h|m|s)?/g);
    let totalSeconds = 0;

    timeMatch.forEach((time) => {
      const unit = time.slice(-1);
      const value = parseInt(time.slice(0, -1), 10);

      if (unit === 'h') {
        totalSeconds += value * 3600;
      } else if (unit === 'm') {
        totalSeconds += value * 60;
      } else {
        totalSeconds += parseInt(time, 10);
      }
    });

    return totalSeconds;
  }

  onYTPlayerReady(event) {
    // Autoplay when YouTube player is ready
    event.target.playVideo();
  }

  playYouTubeVideo() {
    if (this.ytPlayer && typeof this.ytPlayer.playVideo === 'function') {
      this.ytPlayer.playVideo();
    }
  }

  extractYouTubeId(url) {
    const regExp = /(?:youtube\.com\/(?:embed\/|[^/]+\/.+|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^#&?]{11})/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  // TikTok handling with postMessage API for autoplay and unmute
  isTikTok(url) {
    return url.includes('tiktok.com');
  }

  loadTikTokVideo(url, variantId) {
    const tiktokEmbedUrl = `https://www.tiktok.com/embed/${this.extractTikTokId(url)}`;
    if (tiktokEmbedUrl) {
      const embed = document.createElement('iframe');
      embed.src = tiktokEmbedUrl;
      embed.id = `tiktok-player-${variantId}`;
      embed.classList.add('embed-iframe');
      embed.setAttribute('frameborder', '0');
      embed.setAttribute('allowfullscreen', '');
      embed.setAttribute('allow', 'autoplay');

      this.el().appendChild(embed);

      // Function to unmute the video
      const unmuteTikTok = () => {
        try {
          const iframe = document.getElementById(`tiktok-player-${variantId}`);
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                'x-tiktok-player': true,
                type: 'unMute',
                value: '',
              },
              '*',
            );
          }
        } catch (error) {
          console.error('Failed to send unMute postMessage to TikTok player:', error);
        }
      };

      // Listen for postMessage events from the TikTok player
      window.addEventListener('message', (event) => {
        if (event.origin.includes('tiktok.com') && event.data && event.data['x-tiktok-player']) {
          // Check for 'onPlayerReady' to confirm video is loaded
          if (event.data.type === 'onPlayerReady') {
            console.log('TikTok Player Ready');
          }

          // Check for 'onStateChange' to detect play state
          if (event.data.type === 'onStateChange' && event.data.value === 1) {
            // 1 = playing
            console.log('TikTok Player is playing');
            unmuteTikTok(); // Unmute when video starts playing
          }
        }
      });
    } else {
      this.fallbackEmbed(url);
    }
  }

  extractTikTokId(url) {
    const regExp = /tiktok\.com\/[@\w]+\/video\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  // Vimeo handling with correct allow attribute
  isVimeo(url) {
    return url.includes('vimeo.com');
  }

  loadVimeoVideo(url) {
    const videoId = this.extractVimeoId(url);
    if (videoId) {
      const vimeoPlayer = document.createElement('iframe');
      vimeoPlayer.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=0&title=0&byline=0&portrait=0`;
      vimeoPlayer.classList.add('embed-iframe');
      vimeoPlayer.setAttribute('frameborder', '0');
      vimeoPlayer.setAttribute('allowfullscreen', '');
      vimeoPlayer.setAttribute('allow', 'autoplay;');
      this.el().appendChild(vimeoPlayer);
    } else {
      this.fallbackEmbed(url);
    }
  }

  extractVimeoId(url) {
    const regExp = /vimeo\.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  // Loom handling
  isLoom(url) {
    return url.includes('loom.com');
  }

  loadLoomVideo(url) {
    const loomEmbedUrl = url.replace('loom.com/share/', 'loom.com/embed/');
    if (loomEmbedUrl) {
      const embed = document.createElement('iframe');
      embed.src = `${loomEmbedUrl}?autoplay=1&hideEmbedTopBar=true&hide_title=true&hide_owner=true&hide_share=true`;
      embed.classList.add('embed-iframe');
      embed.setAttribute('frameborder', '0');
      embed.setAttribute('allowfullscreen', '');
      embed.setAttribute('allow', 'autoplay;');
      this.el().appendChild(embed);
    } else {
      this.fallbackEmbed(url);
    }
  }

  // Instagram handling
  isInstagram(url) {
    return url.includes('instagram.com');
  }

  loadInstagramContent(url) {
    // Extract aspect ratio setting and remove it from the URL
    let maxWidth;
    if (url.includes('?portrait') || url.includes('&portrait')) {
      maxWidth = '56.25vh'; // Portrait mode
      url = url.replace(/(\?|&)portrait/, '');
    } else if (url.includes('?landscape') || url.includes('&landscape')) {
      maxWidth = '167vh'; // Landscape mode allowing for top banner
      url = url.replace(/(\?|&)landscape/, '');
    } else if (url.includes('?square') || url.includes('&square')) {
      maxWidth = '100vh'; // Square mode
      url = url.replace(/(\?|&)square/, '');
    } else {
      maxWidth = '56.25vh'; // Default to portrait if no parameter provided
    }

    // Construct the Instagram embed URL without the aspect ratio parameter
    const instagramEmbedUrl = url.replace(/instagram\.com\/(reel|p)\//, 'instagram.com/$1/') + 'embed';

    if (instagramEmbedUrl) {
      const embed = document.createElement('iframe');
      embed.src = instagramEmbedUrl;
      embed.classList.add('embed-iframe');
      embed.setAttribute('frameborder', '0');
      embed.setAttribute('allowfullscreen', '');
      embed.setAttribute('allow', 'autoplay;');

      // Apply max-width based on the aspect ratio setting
      embed.style.maxWidth = maxWidth;
      embed.style.margin = '0 auto'; // Center the iframe

      this.el().appendChild(embed);
    } else {
      this.fallbackEmbed(url);
    }
  }

  // LinkedIn handling
  isLinkedIn(url) {
    return url.includes('linkedin.com');
  }

  loadLinkedInPost(url) {
    const embed = document.createElement('iframe');
    embed.src = `https://www.linkedin.com/embed/feed/update/${this.extractLinkedInPostId(url)}`;
    embed.classList.add('embed-iframe');
    embed.setAttribute('frameborder', '0');
    embed.setAttribute('allowfullscreen', '');
    embed.setAttribute('allow', 'autoplay;');
    this.el().appendChild(embed);
  }

  extractLinkedInPostId(url) {
    const regExp = /urn:li:ugcPost:(\d+)/;
    const match = url.match(regExp);
    return match ? `urn:li:ugcPost:${match[1]}` : null;
  }

  // Handle full <iframe> embeds directly
  isIframeEmbed(url) {
    return url.startsWith('<iframe');
  }

  loadIframeEmbed(iframeCode) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(iframeCode, 'text/html');
    const iframe = doc.querySelector('iframe');
    const src = iframe ? iframe.getAttribute('src') : null;

    if (src && src.startsWith('https://')) {
      this.fallbackEmbed(src);
    }
  }

  // Handle Instagram blockquotes
  isInstagramBlockquote(url) {
    return url.startsWith('<blockquote class="instagram-media"');
  }

  loadInstagramBlockquote(blockquoteCode) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(blockquoteCode, 'text/html');
    const blockquote = doc.querySelector('blockquote.instagram-media');
    const permalink = blockquote ? blockquote.getAttribute('data-instgrm-permalink') : null;

    if (permalink && permalink.startsWith('https://')) {
      this.fallbackEmbed(permalink);
    }
  }

  // Handle TikTok blockquotes
  isTikTokBlockquote(url) {
    return url.startsWith('<blockquote class="tiktok-embed"');
  }

  loadTikTokBlockquote(blockquoteCode) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(blockquoteCode, 'text/html');
    const blockquote = doc.querySelector('blockquote.tiktok-embed');
    const videoId = blockquote ? blockquote.getAttribute('data-video-id') : null;

    if (videoId) {
      const tiktokEmbedUrl = `https://www.tiktok.com/embed/${videoId}`;
      this.fallbackEmbed(tiktokEmbedUrl);
    }
  }

  // Fallback embed if URL doesn't match expected formats
  fallbackEmbed(url) {
    if (url.startsWith('https://')) {
      const embed = document.createElement('iframe');
      embed.src = url;
      embed.classList.add('embed-iframe');
      embed.setAttribute('frameborder', '0');
      embed.setAttribute('allowfullscreen', '');
      embed.setAttribute('allow', 'autoplay; microphone; camera; xr-spatial-tracking; accelerometer; gyroscope');
      this.el().appendChild(embed);
    }
  }
}
